/**
 * MUSIQUE — la couche Web Audio qui obéit à `core/musicPolicy` (ROADMAP 12.9).
 *
 * ══ Pourquoi Web Audio et pas Howler ════════════════════════════════════════
 *
 * Howler joue des FICHIERS. Ici il n'y a pas de fichier : chaque note est synthétisée.
 * Faire jouer des sons générés à une bibliothèque de lecture reviendrait à écrire les
 * échantillons dans un buffer pour les lui rendre — trois couches pour une sinusoïde.
 *
 * Les bruitages restent chez Howler, et c'est la bonne frontière : des fichiers d'un côté,
 * de la synthèse de l'autre.
 *
 * ══ Ce que cette couche ne décide PAS ═══════════════════════════════════════
 *
 * Ni les notes, ni le rythme, ni la réponse au cycle du jour — tout cela vit dans
 * `core/musicPolicy`, pur et testé. Ici il n'y a que des oscillateurs, des enveloppes et
 * une horloge. Si la musique sonne faux, le défaut est dans la politique ; si elle
 * craque, il est ici.
 *
 * ══ Trois pièges du Web Audio, traités nommément ════════════════════════════
 *
 * · **L'autoplay est bloqué.** Un `AudioContext` créé avant un geste du joueur naît
 *   `suspended` et n'émet rien — sans erreur. On ne le crée donc qu'au premier
 *   démarrage demandé, et on le reprend explicitement.
 *
 * · **Une enveloppe carrée CLAQUE.** Passer le gain de 0 à 0,5 instantanément produit un
 *   clic à chaque note, qu'on entend d'autant mieux que le fond est calme. Chaque note a
 *   donc une attaque et une chute exponentielles.
 *
 * · **Un onglet masqué gèle les minuteries.** On ne programme donc qu'une mesure à
 *   l'avance, et on la reprogramme à l'horloge de l'`AudioContext` — la seule qui ne
 *   dérive pas quand le navigateur ralentit les `setTimeout`.
 */

import { BEATS_PER_BAR, BEAT_MS, composeBar } from '@/core/musicPolicy';

export interface MusicHandle {
  /** Démarre, ou reprend. Doit être appelé depuis un geste du joueur. */
  start(): void;
  stop(): void;
  setVolume(value: number): void;
  /** Moment de la journée, 0 = minuit — relayé tel quel à la politique. */
  setPhase(phase: number): void;
  isPlaying(): boolean;
  destroy(): void;
}

export function createMusic(): MusicHandle {
  let context: AudioContext | null = null;
  let master: GainNode | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let playing = false;
  let volume = 0.5;
  let phase = 0.5;
  let last = 7;

  /** Une note : oscillateur doux, enveloppe sans angle. */
  const voice = (hz: number, at: number, durationMs: number, gain: number): void => {
    if (context === null || master === null) return;

    const osc = context.createOscillator();
    /*
      Triangle et non sinus : le sinus pur sonne synthétique et « creux » sur un haut-
      parleur de téléphone, qui ne restitue presque rien sous 200 Hz. Le triangle porte
      quelques harmoniques impaires — assez pour exister, pas assez pour être agressif.
    */
    osc.type = 'triangle';
    osc.frequency.value = hz;

    const envelope = context.createGain();
    const duration = durationMs / 1000;
    // Attaque LENTE : c'est elle qui transforme une note en nappe. À 25 ms on entend
    // une attaque ; à 600 ms on entend une respiration.
    const attack = Math.min(0.6, duration * 0.4);

    envelope.gain.setValueAtTime(0.0001, at);
    envelope.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), at + attack);
    envelope.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    osc.connect(envelope).connect(master);
    osc.start(at);
    osc.stop(at + duration + 0.05);
  };

  /** Programme la mesure suivante, puis se rappelle. */
  const schedule = (): void => {
    if (context === null || !playing) return;

    const bar = composeBar(last, phase, BEATS_PER_BAR, Math.random);
    last = bar.last;

    const now = context.currentTime;
    for (const note of bar.notes) {
      voice(note.hz, now + note.delayMs / 1000, note.durationMs, note.gain);
    }

    /*
      On reprogramme un peu AVANT la fin de la mesure. Attendre pile la fin laisserait un
      trou audible dès que le navigateur retarde la minuterie de quelques dizaines de
      millisecondes — ce qu'il fait dès que l'onglet perd le focus.
    */
    timer = setTimeout(schedule, BEATS_PER_BAR * BEAT_MS - 400);
  };

  return {
    start() {
      if (playing) return;
      /*
        ⚠️ L'`AudioContext` se crée ICI, au geste, jamais au chargement du module. Créé
        trop tôt il naît `suspended` sur tous les navigateurs mobiles, et rien ne sort —
        sans la moindre erreur en console. C'est la même famille de défaut que la police
        Nunito : déployée, servie, valide, et totalement inerte (règle 11).
      */
      context ??= new AudioContext();
      if (context.state === 'suspended') void context.resume();

      master ??= context.createGain();
      master.gain.value = volume * 0.18; // La musique reste SOUS les bruitages.
      master.connect(context.destination);

      playing = true;
      schedule();
    },

    stop() {
      playing = false;
      clearTimeout(timer);
      // On coupe en fondu : un `disconnect` sec sur une note tenue claque autant qu'une
      // enveloppe carrée.
      if (master !== null && context !== null) {
        master.gain.linearRampToValueAtTime(0.0001, context.currentTime + 0.4);
      }
    },

    setVolume(value) {
      volume = Math.max(0, Math.min(1, value));
      if (master !== null && context !== null) {
        master.gain.linearRampToValueAtTime(volume * 0.18, context.currentTime + 0.2);
      }
    },

    setPhase(value) {
      phase = value;
    },

    isPlaying: () => playing,

    destroy() {
      playing = false;
      clearTimeout(timer);
      master?.disconnect();
      void context?.close();
      context = null;
      master = null;
    },
  };
}
